import * as React from 'react';
import cn from 'classnames';
import { Sticky, StickyContainer } from 'react-sticky';
import {
    DWTInquiryModal,
    HeaderContext,
    HeaderContextType,
    InquiryModal,
    InquiryType,
    RFPContainer,
    SubHeaderTypes,
    UserInfo,
    Slider,
    ReportDMCAModal,
} from '@weddingspot/ws-core-components';
import { Venues } from '@weddingspot/ws-api-client';

import Panel from '../panel';
import StickySpotEstimate from '../sticky-spot-estimate';

import SecondaryCTA from '../secondary-cta';
import VenueHightlights, { VenueHighlight } from '../venue-highlights';
import VenueDetails from '../venue-details';
import VenueAdditionalDetails from '../venue-additional-details';
import { VenueInterestedCTA as InterestedCTA, VenuePrimaryCTA as PrimaryCTA } from '../venue-cta';
import { default as RecommendedVenues, RecommendedVenuesProps } from '../venue-recommendations';
import ServiceOptionsModal from '../service-options-modal';

import SubHeader from '../sub-header';
import { ServiceProps } from '../service-options-modal/Services';
import { DiscountProps } from '../venue-additional-details/Discounts';
import { VenueLinksProps } from '../venue-additional-details/Links';
import { WeddingPackageProps } from '../venue-additional-details/packages/Packages';
import { EventProps } from '../venue-additional-details/upcoming-events/';
import { HBToastContainer, hbToastInfo } from '../toast';
import { Zoom } from 'react-toastify';
import { isMobile } from 'react-device-detect';
import VenueProfileNotification = Venues.VenueProfileNotification;

const styles = require('./styles.scss');

export interface IProps extends VenueLinksProps, RecommendedVenuesProps {
    sessionId: string;

    onAccordionExpanded?: (name: string) => void;
    areAccordionsOpen: boolean;
    // ---- slider props
    images: { src: string; credit: string }[];
    activeImageIndex: number;
    onNextSlide: () => void;
    onPrevSlide: () => void;
    onActiveImageChange?: (index: number) => void;

    // END slider props

    // Venue details
    name: string;
    venueId: number;
    venueRegion: string;
    venueMarket: string;
    phoneNumber?: string;
    onPhoneClick: () => void;
    onCallClick: () => void;
    isFavorite: boolean;
    description: string;
    awards: Venues.VenueAward[];
    estimateCompleted?: boolean;
    venueCSNId?: string;

    // Whether or not this venue profile is a rehearsal dinners profile
    renderRdv: boolean;

    styles: {
        id: number;
        name: string;
    }[];
    videos: string[];
    ceremonies: ServiceProps[];
    receptions: ServiceProps[];
    rds: ServiceProps[];

    city: string;
    state: string;
    address_1: string | null;
    address_2: string | null;

    // for international addresses
    // i.e. destination weddings
    province: string | null;
    zip_code: string | null;
    country: string;
    // Note: This means the venue is a DW.com venue, not a WS.com destination wedding venue
    is_destination: boolean;

    lng: number;
    ltd: number;

    venueNotes: string;

    amenities: {
        id: number;
        name: string;
    }[];
    restrictions: {
        id: number;
        name: string;
    }[];
    maxCapacity?: number;
    minCapacity?: number;
    availabilityLastUpdated?: string;
    highlights: VenueHighlight[];
    discounts: DiscountProps[];

    // Estimate and budget props
    isPriced: boolean;
    isDestination: boolean;
    startingPricePerPersonRDs?: number;
    hideWeddingCosts?: boolean;
    hideGuestRooms?: boolean;

    spotEstimateTotal?: number;
    budgetMin?: number;
    budgetCapacity?: number;

    onFavoriteClick: (target: boolean) => Promise<boolean>;
    availability: { [date: string]: number } | null;
    userIsAuthed: boolean;

    disableRecommendations?: boolean;
    showAdditionalPriceVenueButton?: boolean;
    showGuestRoomsRecommendations?: boolean;
    showWsRfpForm?: boolean;
    showSleepingRoomsOnVenueProfile?: boolean;

    // testRecsCount?: 4 | 6;
    testGuestRoomsIntentCta?: 'A' | 'B';
    intentCTAonPostOutreachStep1?: 'A' | 'B';
    simplifyOutreachTestValue2?: 'A' | 'B';
    intentCTAStepRemoved?: 'A' | 'B';

    notifications?: VenueProfileNotification[];

    // Packages
    packages: WeddingPackageProps[];

    // Upcoming events
    upcoming_events: EventProps[];

    profileImagesLazyLoad?: boolean;
    showShoppingCart?: boolean;

    // Inquiry modal
    inquiryModalShowing: boolean;
    shouldShowInquiryAbandonment?: () => boolean;
    showInquiryModal: (isRDVInquiry?: boolean) => void;
    hideInquiryModal: () => void;
    handleNavigatedToDWTInquiryFormStep2?: () => void;
    handleNavigatedToConnectAccount?: () => void;
    handleNavigatedToPostOutreachScreen?: () => void;
    inquiryFormConnectAccountSuccess?: () => void;
    inquiryFormSubmitSuccess?: (isQuote: boolean) => void;
    inquiryModalRecommendationClicked?: (position: number, recommendations: Venues.VenueRecommendation[]) => void;
    handleCloseClicked?: (screen?: string) => void;
    handleAbandonmentShown?: () => void;
    handleAbandonmentCtaClicked?: () => void;
    handleAbandonmentDismissed?: () => void;
    handleAddVenueToShoppingCart?: () => void;
    handleRemoveVenueFromShoppingCart?: () => void;
    pushOutreachNoThanksClicked?: () => void;
    pushOutreachSuccessBrowseMoreClicked?: () => void;
    pushOutreachInquireClicked?: (position: number[]) => void;
    pushIntentCTAonPostOutreachStep1?: () => void;
    handleNavigatedToSeeNearbyHotels?: (testGuestRoomsIntentCta?: 'A' | 'B') => void;
    handleNavigatedToHotels?: (count: number) => void;
    handleRejectSeeHotels?: () => void;
    handleRejectHotelsRecommended?: () => void;
    handleHotelsRecommendedRFP?: () => void;
    handleHotelsRecommendedForm: (source: string, count: number) => void;
    handleRoomCountSubmit: (params: object) => void;
    handleClickedSpecifyGuestRooms: (source?: string) => void;
    isEmailExists?: () => void;
    userInfo?: UserInfo;
}

interface State {
    forceRDInquiry: boolean;
    reportModalShowing: boolean;
    serviceOptionsModalShowing: boolean;
    RFPShowing: boolean;
}

const initialState: State = {
    forceRDInquiry: false,
    reportModalShowing: false,
    serviceOptionsModalShowing: false,
    RFPShowing: false,
};

export class VenuePage extends React.Component<IProps, State> {
    readonly state: State = initialState;
    static contextType: React.Context<HeaderContextType> = HeaderContext;
    context!: React.ContextType<typeof HeaderContext>;

    constructor(props: IProps) {
        super(props);
        this.closeInquiryModal = this.closeInquiryModal.bind(this);
        this.openInquiryModal = this.openInquiryModal.bind(this);
        this.openRDInquiryModal = this.openRDInquiryModal.bind(this);
        this.closeReportModal = this.closeReportModal.bind(this);
        this.openReportModal = this.openReportModal.bind(this);
        this.closeServiceOptionsModal = this.closeServiceOptionsModal.bind(this);
        this.openServiceOptionsModal = this.openServiceOptionsModal.bind(this);
        this.onPackageClick = this.onPackageClick.bind(this);
        this.handleNavigatedToDWTInquiryFormStep2 = this.handleNavigatedToDWTInquiryFormStep2.bind(this);
        this.showTravelNotification = this.showTravelNotification.bind(this);
    }

    componentDidUpdate(prevProps: IProps) {
        if (this.props.notifications !== undefined && prevProps.notifications === undefined) {
            this.showTravelNotification();
        }
    }

    renderInterestingCta() {
        const showAdditionalPriceVenueButton = this.props.showAdditionalPriceVenueButton && !this.props.renderRdv;
        return (
            <Panel>
                <InterestedCTA
                    isPriced={this.props.isPriced}
                    onClickContact={this.openInquiryModal}
                    showAdditionalPriceVenueButton={showAdditionalPriceVenueButton}
                    estimateTotal={this.props.spotEstimateTotal}
                    venueId={this.props.venueId}
                    // Shopping Card Props
                    venueName={this.props.name}
                    venueLocation={`${this.props.city}, ${this.props.state}`}
                    venueImageUrl={this.props.images[0]?.src}
                    shoppingListAllowed={this.props.showShoppingCart}
                    handleAddVenueToShoppingCart={this.props.handleAddVenueToShoppingCart}
                    handleRemoveVenueFromShoppingCart={this.props.handleRemoveVenueFromShoppingCart}
                    sourcePage='Venue Profile Page'
                />
            </Panel>
        );
    }

    public render() {
        // For responsive layout this components
        // are injected in a few places on the page.
        // Bind them to variables with props in order
        // to do not pass them twice.
        // Note: for secondary CTA, there's an h1 tag for the venue name

        const shouldRenderPrimaryCTAOnMain = !this.props.renderRdv &&
            (this.props.isPriced || (!!this.props.spotEstimateTotal && this.props.showShoppingCart ));
        const primaryCTA = (
            <PrimaryCTA
                venueId={this.props.venueId}
                isPriced={this.props.isPriced}
                isRdvProfile={this.props.renderRdv}
                startingPricePerPersonRDs={this.props.startingPricePerPersonRDs}
                budgetRangeMin={this.props.budgetMin}
                budgetRangeCapacity={this.props.budgetCapacity}
                estimateTotal={this.props.spotEstimateTotal}
                onClickContact={this.openInquiryModal}
                onClickQuote={this.openInquiryModal}
                hideWeddingCosts={this.props.hideWeddingCosts}
                // Shopping Card Props
                venueName={this.props.name}
                venueLocation={`${this.props.city}, ${this.props.state}`}
                venueImageUrl={this.props.images[0]?.src}
                shoppingListAllowed={this.props.showShoppingCart}
                sourcePage='Venue Profile Page'
            />
        );

        const highlights = <VenueHightlights highlights={this.props.highlights} />;

        return (
            <div className={styles.className}>
                <HBToastContainer showCloseButton={true} />
                <StickyContainer>
                    <SubHeader headerType={SubHeaderTypes.venueProfile} />

                    <div className={styles.container}>
                        <div className={styles.mainContent}>
                            <Slider
                                videos={this.props.videos}
                                venueName={this.props.name}
                                venueRegion={this.props.venueRegion}
                                venueId={this.props.venueId}
                                isPricedVenue={this.props.isPriced}
                                onReportClick={this.openReportModal}
                                onActiveImageChange={this.props.onActiveImageChange}
                                images={this.props.images}
                                onContactClick={this.openInquiryModal}
                                estimateComplete={this.props.isPriced && !!this.props.spotEstimateTotal}
                                profileImagesLazyLoad={this.props.profileImagesLazyLoad}
                                renderCtaSlide={true}
                                showCounter={true}
                            />

                            <div className={styles.visibleMobile}>
                                <SecondaryCTA
                                    name={this.props.name}
                                    nameAsH1={isMobile}
                                    phoneNumber={this.props.phoneNumber}
                                    isFavorite={this.props.isFavorite}
                                    handleOnFavoriteClick={this.props.onFavoriteClick}
                                    handleOnCallClick={this.props.onCallClick}
                                    handleOnPhoneClick={this.props.onPhoneClick}
                                />
                                {this.props.highlights.length > 0 && (
                                    <Panel>
                                        <h3 className={styles.panelHeadline}>Venue highlights</h3>
                                        {highlights}
                                    </Panel>
                                )}
                            </div>

                            <Panel>
                                <h3 className={styles.panelHeadline}>Venue details</h3>
                                <VenueDetails
                                    name={this.props.name}
                                    address={{
                                        address1: !this.props.address_1 ? undefined : this.props.address_1,
                                        address2: !this.props.address_2 ? undefined : this.props.address_2,
                                        province: this.props.province,
                                        zip_code: this.props.zip_code,
                                        country: this.props.country,
                                        city: this.props.city,
                                        state: this.props.state,
                                    }}
                                    capacityMax={this.props.maxCapacity}
                                    capacityMin={this.props.minCapacity}
                                    styles={this.props.styles}
                                    awards={this.props.awards}
                                    description={this.props.description}
                                    ceremonies={this.props.ceremonies}
                                    receptions={this.props.receptions}
                                    rds={this.props.rds}
                                    handleServiceOptionsClick={this.openServiceOptionsModal}
                                />
                            </Panel>

                            <Panel>
                                <h3 className={styles.panelHeadline}>Additional details</h3>
                                <VenueAdditionalDetails
                                    venueId={this.props.venueId}
                                    venueCSNId={this.props.venueCSNId}
                                    onAccordionExpanded={this.props.onAccordionExpanded}
                                    areAccordionsOpen={this.props.areAccordionsOpen}
                                    availability={this.props.availability}
                                    availabilityLastUpdated={this.props.availabilityLastUpdated}
                                    venueNotes={this.props.venueNotes}
                                    amenities={this.props.amenities}
                                    restrictions={this.props.restrictions}
                                    ceremonies={this.props.ceremonies}
                                    receptions={this.props.receptions}
                                    rds={this.props.rds}
                                    discounts={this.props.discounts}
                                    websiteUrl={this.props.websiteUrl}
                                    onWebsiteClick={this.props.onWebsiteClick}
                                    facebookUrl={this.props.facebookUrl}
                                    instagramUrl={this.props.instagramUrl}
                                    pinterestUrl={this.props.pinterestUrl}
                                    onSocialClick={this.props.onSocialClick}
                                    packages={this.props.packages}
                                    onClickPackageInquire={this.onPackageClick}
                                    onClickInquire={this.openRDInquiryModal}
                                    upcomingEvents={this.props.upcoming_events}
                                    showSleepingRoomsOnVenueProfile={this.props.showSleepingRoomsOnVenueProfile}
                                    onSleepingRoomsSelect={() => {
                                        this.setState({ RFPShowing: true });
                                    }}
                                    address={!this.props.address_1 ? undefined : this.props.address_1}
                                    state={!this.props.state ? undefined : this.props.state}
                                    city={!this.props.city ? undefined : this.props.city}
                                    zipCode={!this.props.zip_code ? undefined : this.props.zip_code}
                                    hideGuestRooms={this.props.hideGuestRooms}
                                />
                            </Panel>

                            {!this.props.spotEstimateTotal ? '' : this.renderInterestingCta()}

                            {!this.props.disableRecommendations &&
                                this.props.recommendationsLoadSucceeded !== false &&
                                this.props.recommendations.length > 0 && (
                                    <Panel>
                                        <RecommendedVenues
                                            venueId={this.props.venueId}
                                            logRecommendation={true}
                                            recommendations={this.props.recommendations}
                                            recommendationsLoadSucceeded={this.props.recommendationsLoadSucceeded}
                                            onRecommendationClick={this.props.onRecommendationClick}
                                            renderRdv={this.props.renderRdv}
                                        />
                                    </Panel>
                                )}
                        </div>

                        <div className={styles.sidebar}>
                            <Panel>
                                <SecondaryCTA
                                    name={this.props.name}
                                    nameAsH1={!isMobile}
                                    phoneNumber={this.props.phoneNumber}
                                    isFavorite={this.props.isFavorite}
                                    handleOnFavoriteClick={this.props.onFavoriteClick}
                                    handleOnCallClick={this.props.onCallClick}
                                    handleOnPhoneClick={this.props.onPhoneClick}
                                />
                                {this.props.highlights.length > 0 && <div className={cn(styles.hiddenMobile, styles.line)} />}
                                {this.props.highlights.length > 0 && <h3 className={styles.panelHeadline}>Venue Highlights</h3>}
                                {this.props.highlights.length > 0 && highlights}
                            </Panel>

                            {/* value of topOffset prop is the approximate height from
                                the top of the profile page content and the top of this button */}
                            <Sticky topOffset={420}>
                                {({ style }) => (
                                    <div
                                        style={{
                                            ...style,
                                            marginTop: 10,
                                        }}
                                    >
                                        {shouldRenderPrimaryCTAOnMain && (
                                            <Panel>
                                                {primaryCTA}
                                            </Panel>
                                        )}
                                        {!this.props.spotEstimateTotal ? this.renderInterestingCta() : ''}
                                    </div>
                                )}
                            </Sticky>
                        </div>

                        <div className={styles.visibleMobile}>
                            <StickySpotEstimate
                                venueId={this.props.venueId}
                                isPriced={this.props.isPriced}
                                isRdvProfile={this.props.renderRdv}
                                startingPricePerPersonRDs={this.props.startingPricePerPersonRDs}
                                budgetRangeMin={this.props.budgetMin}
                                budgetRangeCapacity={this.props.budgetCapacity}
                                estimateTotal={this.props.spotEstimateTotal}
                                onClickContact={this.openInquiryModal}
                                onClickQuote={this.openInquiryModal}
                            />
                        </div>
                    </div>

                    {/* RFP use defaultFormValue on componentDidMount,
                    so we should not render it immediately */}
                    {this.state.RFPShowing && (
                        <RFPContainer
                            show={this.state.RFPShowing}
                            onHide={() => this.setState({ RFPShowing: false })}
                            csnVenueData={this.props.venueCSNId ? [{ id: this.props.venueCSNId }] : []}
                            defaultFormValue={this.props.userInfo}
                            handleHotelsRecommendedForm={(count) => this.props.handleHotelsRecommendedForm('wedding venue profile', count)}
                            handleRoomCountSubmit={this.props.handleRoomCountSubmit}
                            handleClickedSpecifyGuestRooms={() => {
                                this.props.handleClickedSpecifyGuestRooms('Venue Profile');
                            }}
                            leadSource='profile page'
                        />
                    )}
                    <ServiceOptionsModal
                        primaryCTAComponent={primaryCTA}
                        show={this.state.serviceOptionsModalShowing}
                        ceremonies={this.props.ceremonies}
                        receptions={this.props.receptions}
                        isPricedVenue={this.props.isPriced}
                        rds={this.props.rds}
                        onClose={this.closeServiceOptionsModal}
                        onCtaClick={() => {
                            window.location.href = `/pricing/${this.props.venueId}/estimate/`;
                        }}
                        onClickInquire={this.openRDInquiryModal}
                    />

                    {this.renderInquiryModal()}

                    <ReportDMCAModal
                        venueId={this.props.venueId}
                        showModal={this.state.reportModalShowing}
                        handleClose={this.closeReportModal}
                    />
                </StickyContainer>
            </div>
        );
    }

    private renderInquiryModal() {
        const simplifyOutreachTestValue2 = this.props.simplifyOutreachTestValue2;

        const hidePhoneAndReasonFields = this.props.venueMarket !== 'Destination Weddings';

        const showPhoneAndHideReason = simplifyOutreachTestValue2 === 'B';
        const { headerData } = this.context;
        const returnToResultsLink = headerData?.return_to_results_link || '';

        const { handleNavigatedToSeeNearbyHotels, testGuestRoomsIntentCta } = this.props;

        if (this.props.isDestination) {
            return (
                <DWTInquiryModal
                    venueId={this.props.venueId}
                    showModal={this.props.inquiryModalShowing}
                    shouldShowAbandonment={this.props.shouldShowInquiryAbandonment}
                    handleModalClosed={this.closeInquiryModal}
                    onGoToStep2={this.handleNavigatedToDWTInquiryFormStep2}
                    handleNavigatedToConnectAccount={this.props.handleNavigatedToConnectAccount}
                    handleNavigatedToPostOutreachScreen={this.props.handleNavigatedToPostOutreachScreen}
                    handleConnectAccountSuccess={this.props.inquiryFormConnectAccountSuccess}
                    handleInquirySubmitSuccess={this.props.inquiryFormSubmitSuccess}
                    handleRecommendationsClick={this.props.inquiryModalRecommendationClicked}
                    handleCloseClicked={this.props.handleCloseClicked}
                    handleAbandonmentShown={this.props.handleAbandonmentShown}
                    handleAbandonmentCta={this.props.handleAbandonmentCtaClicked}
                    handleAbandonmentDismiss={this.props.handleAbandonmentDismissed}
                    closestSRPURL={returnToResultsLink}
                    isDestination={true}
                    showPhoneAndHideReason={true}
                    showGuestRoomsRecommendations={this.props.showGuestRoomsRecommendations}
                    testGuestRoomsIntentCta={this.props.testGuestRoomsIntentCta}
                    pushIntentCTAonPostOutreachStep1={this.props.pushIntentCTAonPostOutreachStep1}
                    intentCTAonPostOutreachStep1={this.props.intentCTAonPostOutreachStep1}
                    showWsRfpForm={this.props.showWsRfpForm}
                    handleNavigatedToSeeNearbyHotels={() => {
                        handleNavigatedToSeeNearbyHotels && handleNavigatedToSeeNearbyHotels(testGuestRoomsIntentCta);
                    }}
                    handleNavigatedToHotels={this.props.handleNavigatedToHotels}
                    handleRejectSeeHotels={this.props.handleRejectSeeHotels}
                    handleRejectHotelsRecommended={this.props.handleRejectHotelsRecommended}
                    handleHotelsRecommendedRFP={this.props.handleHotelsRecommendedRFP}
                    handleHotelsRecommendedForm={(count) => this.props.handleHotelsRecommendedForm('post outreach modal', Number(count))}
                    handleRoomCountSubmit={this.props.handleRoomCountSubmit}
                    handleClickedSpecifyGuestRooms={() => {
                        this.props.handleClickedSpecifyGuestRooms('Post outreach flow');
                    }}
                />
            );
        }
        return (
            <InquiryModal
                venueId={this.props.venueId}
                inquiryType={this.props.renderRdv || this.state.forceRDInquiry ? InquiryType.REHEARSAL_DINNER : InquiryType.WEDDING}
                showModal={this.props.inquiryModalShowing}
                userIsAuthed={this.props.userIsAuthed}
                shouldShowAbandonment={this.props.shouldShowInquiryAbandonment}
                handleModalClosed={this.closeInquiryModal}
                handleNavigatedToPostOutreachScreen={this.props.handleNavigatedToPostOutreachScreen}
                handleConnectAccountSuccess={this.props.inquiryFormConnectAccountSuccess}
                handleInquirySubmitSuccess={this.props.inquiryFormSubmitSuccess}
                handleRecommendationsClick={this.props.inquiryModalRecommendationClicked}
                handleCloseClicked={this.props.handleCloseClicked}
                handleAbandonmentShown={this.props.handleAbandonmentShown}
                handleAbandonmentCta={this.props.handleAbandonmentCtaClicked}
                handleAbandonmentDismiss={this.props.handleAbandonmentDismissed}
                closestSRPURL={returnToResultsLink}
                pushOutreachNoThanksClicked={this.props.pushOutreachNoThanksClicked}
                pushOutreachSuccessBrowseMoreClicked={this.props.pushOutreachSuccessBrowseMoreClicked}
                pushOutreachInquireClicked={this.props.pushOutreachInquireClicked}
                pushIntentCTAonPostOutreachStep1={this.props.pushIntentCTAonPostOutreachStep1}
                isDestination={false}
                hidePhoneNumberField={hidePhoneAndReasonFields && !showPhoneAndHideReason}
                hideReasonForOutreachField={hidePhoneAndReasonFields && !showPhoneAndHideReason}
                showPhoneAndHideReason={showPhoneAndHideReason}
                showGuestRoomsRecommendations={this.props.showGuestRoomsRecommendations}
                testGuestRoomsIntentCta={this.props.testGuestRoomsIntentCta}
                showWsRfpForm={this.props.showWsRfpForm}
                intentCTAonPostOutreachStep1={this.props.intentCTAonPostOutreachStep1}
                handleNavigatedToSeeNearbyHotels={() => {
                    handleNavigatedToSeeNearbyHotels && handleNavigatedToSeeNearbyHotels(testGuestRoomsIntentCta);
                }}
                handleNavigatedToHotels={this.props.handleNavigatedToHotels}
                handleRejectSeeHotels={this.props.handleRejectSeeHotels}
                handleRejectHotelsRecommended={this.props.handleRejectHotelsRecommended}
                handleHotelsRecommendedRFP={this.props.handleHotelsRecommendedRFP}
                handleHotelsRecommendedForm={(count) => this.props.handleHotelsRecommendedForm('post outreach modal', Number(count))}
                handleRoomCountSubmit={this.props.handleRoomCountSubmit}
                handleClickedSpecifyGuestRooms={() => {
                    this.props.handleClickedSpecifyGuestRooms('Post outreach flow');
                }}
                isEmailExists={this.props.isEmailExists}
                intentCTAStepRemoved={this.props.intentCTAStepRemoved}
            />
        );
    }

    private closeInquiryModal() {
        this.props.hideInquiryModal();
        this.setState({ forceRDInquiry: false });
    }

    private openInquiryModal() {
        this.props.showInquiryModal();
    }

    private openRDInquiryModal() {
        this.props.showInquiryModal(true);
        this.setState({ forceRDInquiry: true });
    }

    private closeReportModal() {
        this.setState({ reportModalShowing: false });
    }

    private openReportModal() {
        this.setState({ reportModalShowing: true });
    }

    private closeServiceOptionsModal() {
        this.setState({ serviceOptionsModalShowing: false });
    }

    private openServiceOptionsModal() {
        this.setState({ serviceOptionsModalShowing: true });
    }

    private onPackageClick(pkg: WeddingPackageProps, idx: number) {
        this.openInquiryModal();
    }

    private handleNavigatedToDWTInquiryFormStep2() {
        // DWT Inquiry form
        this.props.handleNavigatedToDWTInquiryFormStep2 && this.props.handleNavigatedToDWTInquiryFormStep2();
    }

    private showTravelNotification() {
        if (!!this.props.notifications && this.props.notifications.length > 0) {
            this.props.notifications.map((notification) =>
                hbToastInfo(
                    <div className='VenuePage--toast--body VenuePage--toast__info'>
                        <div className='VenuePage--toast--text'>
                            <div className='VenuePage--toast--message'>{notification.message}</div>
                        </div>
                    </div>,
                    {
                        position: 'bottom-right',
                        autoClose: isMobile ? 5000 : false,
                        transition: Zoom,
                    }
                )
            );
        }
    }
}

export default VenuePage;
